import { SocketClient } from '@pw-tech/socket-client';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { MqttClient } from 'precompiled-mqtt';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DoubtSolutionEventData } from 'src/constants/type';
// import { createContext, useContextSelector } from 'use-context-selector';
import videojs from 'video.js';

import { LocalStorageKeys, TESTGROUP, UrlType } from './constants/constants';
import {
  ADDONS_TYPE,
  BATCH_TYPE,
  CLIENT_TYPE,
  COACH_TYPE,
  POLL_CONNECTION_TYPE,
  VideoContainerType,
} from './constants/type';
import { ISessionEvent, SessionEventTypes } from './event-stack';
import useCheckUnleashFeature, { UnleashFeature } from './hooks/useCheckUnleashFeature';
import { usePeriodicSync } from './hooks/usePerioidicSync';
import { logEvent } from './lib/events';
import { EventType } from './lib/events/events';
import eventTracker from './lib/eventTracker/eventTracker';
import {
  fetchScheduledLeaderBoardFromApi,
  fetchUserPollResultFromApi,
  fetchUserPollStatsFromApi,
} from './lib/fetchers/poll-fetchers';
import useActiveSaarthi from './lib/hooks/ask-saarthi/useActiveSaarthi';
import useActiveTab from './lib/hooks/batches/useActiveTab';
import useAddons from './lib/hooks/batches/useAddons';
import useContentDetails, { ContentDetails } from './lib/hooks/content/useContentDetails';
import useIDsAssociatedWithContent from './lib/hooks/content/useIDsAssociatedWithContent';
import { U_SOCKET_CONTEXT } from './lib/hooks/join-meet';
import useFetchActivePoll from './lib/hooks/polls/useFetchActivePoll';
import { PollDetail } from './lib/hooks/polls/useGetPollDetails';
import useGet3DModels, { Model as cModel, Model } from './lib/hooks/threeD-models/useGet3DModels';
import { useAsyncRefProp } from './lib/hooks/useAsyncRef';
import useEventTracker from './lib/hooks/useEvents';
import { useStateRef } from './lib/hooks/useStateRef';
import useFetchStats from './lib/hooks/video-player/useFetchStats';
import useMetaData from './lib/hooks/video-player/useMetaData';
import createMqttService, { OPERATION } from './lib/hooks/video-player/useMqttService';
import useOttFetchStats from './lib/hooks/video-player/useOttFetchStats';
import useVideoUrlDetails from './lib/hooks/video-player/useVideoUrlDetails';
import {
  selectPlayerParams,
  setIsPollFeedbackEnabled,
  updateIsFullScreen,
  useSelector,
} from './lib/redux';
import { getBatchMQTTTopic, getScheduleMQTTTopic } from './lib/socket/events';
import useNotify, {
  NotificationDuration,
  NotificationEnums,
  NotificationPosition,
  NotificationTitles,
  Theme,
} from './lib/useNotify';
import { getPlayerState, isOTT } from './lib/utilities';
import { requestPlayback } from './lib/video-utility';
import { firebaseInitPromise, getValueFromFirebase } from './services/firebase-service';
import { LocalStorageInstance } from './services/localStroage/LocalStorageService';
import { logger } from './services/logger/loggerService';
import { CUSTOM_EVENT_NAMES, pubSub } from './services/pubSub';
import { BatchEvents, EmitContext, ScheduleEvents } from './services/SocketEnums';
import { socketListener } from './services/SocketListener';
import { createVideoUrlDetailParams, isPPAdmin, qualitiesMapping } from './services/utils';
import {
  convertSecondsToHHMMSSFormat,
  GeneratedLeaderBoardModel,
  GeneratedResultModel,
  getActivePollSelectedAnswerFromStorage,
  getExpiryTimeFromStorage,
  getLiveTokenFromStorage,
  getPollDetailsFromStorage,
  getPollIdFromStorage,
  LeaderBoardModel,
  QUESTION_TYPE,
  removeActivePollSelectedAnswerFromStorage,
  removeActivePostPollSelectedAnswerFromStorage,
  removeExpiryTimeFromStorage,
  removeLiveTokenFromStorage,
  removeShowLeaderBoardClickedFromStorage,
  SelfStats,
  setExpiryTimeToStorage,
  setLiveTokenToStorage,
  setPollIdToStorage,
  setPostPollIdToLocalStorage,
} from './shared/core/video/poll-widget/Utils';
import { State, UsePlayerReturn } from './types/player.types';
import { getUserInfo } from './usePlayerParams';

type AnotherPoll = {
  pollTimeFormat: string;
  pollId: string;
};

const initialState = {
  timestamp: 0,
  player: undefined,
  minimal: false,
  user: null,
  playbackRate: 1,
  quality: 'Auto',
  slideMode: '',
  // batchDetails: null,
  scheduleDetails: null,
  url: '',
  isFullScreen: false,
  isSettingsClicked: false,
  isPollIconActive: false,
  activeSaarthiData: {},
  isAskSaarthiEnabled: false,
  showGoToLive: false,
  isLiveStreaming: false,
  askSaarthiSubject: '',
  videoPausedByAddNote: false,
  cwocCoachMentor: '',
  slideDataContext: [],
  threeDModelData: null,
  playerMode: 'LowData',
  isLowDataModeEnabled: false,
  selectedDoubtSlideId: '',
  selectedDoubtId: '',
};

type Action =
  | {
      type: 'TIMESTAMP_UPDATED';
      value: number;
    }
  | {
      type: 'URL_UPDATED';
      value: string;
    }
  | {
      type: 'ASK_SAARTHI_SUBJECT_UPDATED';
      value: string;
    }
  | {
      type: 'SLIDE_MODE_UPDATED';
      value: string;
    }
  | {
      type: 'PLAYER_UPDATED';
      value: any;
    }
  | {
      type: 'MINIMAL_UPDATED';
      value: boolean;
    }
  | {
      type: 'PLAYBACK_RATE_UPDATED';
      value: number;
    }
  | {
      type: 'BLOCK_STATE_UPDATED';
      value: boolean;
    }
  | {
      type: 'QUALITY_UPDATED';
      value: any;
    }
  | {
      type: 'USER_UPDATED';
      value: any;
    }
  | {
      type: 'FULL_SCREEN_UPDATED';
      value: any;
    }
  | {
      type: 'SETTINGS_CLICKED';
      value: any;
    }
  | {
      type: 'SWITCH_CLICKED';
      value: any;
    }
  | {
      type: 'THREE_DOTS_CLICKED';
      value: any;
    }
  | {
      type: 'EMOJI_CHAT_ENABLED_UPDATED';
      value: any;
    }
  | {
      type: 'VIDEO_PLAYER_PAUSED_ON_ADD_NOTE';
      value: any;
    }
  | {
      type: 'SET_CWOC_COACH_MENTOR';
      value: any;
    }
  | {
      type: 'SET_SLIDE_DATA';
      value: any;
    }
  | {
      type: 'SET_PLAYER_MODE';
      value: any;
    }
  | {
      type: 'SET_LOW_DATA_MODE';
      value: any;
    }
  | {
      type: 'FETCH_POLICY_DATA';
      value: any;
    }
  | { type: 'SET_DOUBT_TO_OPEN'; payload: { slideId: string; doubtId: string } };

export const PlayerContext = React.createContext<UsePlayerReturn | undefined>(undefined);
PlayerContext.displayName = 'PlayerContext';

function playerReducer(state: State, action: Action) {
  switch (action.type) {
    case 'TIMESTAMP_UPDATED': {
      return {
        ...state,
        timestamp: action.value,
      };
    }
    case 'URL_UPDATED': {
      return {
        ...state,
        url: action.value,
      };
    }
    case 'SLIDE_MODE_UPDATED': {
      return {
        ...state,
        slideMode: action.value as any,
      };
    }
    case 'USER_UPDATED': {
      return {
        ...state,
        user: action.value as any,
      };
    }
    case 'PLAYER_UPDATED': {
      return {
        ...state,
        player: action.value,
      };
    }
    case 'MINIMAL_UPDATED': {
      return {
        ...state,
        minimal: action.value,
      };
    }
    case 'BLOCK_STATE_UPDATED': {
      return {
        ...state,
        batchBlockStateChange: action.value,
      };
    }
    case 'PLAYBACK_RATE_UPDATED': {
      return {
        ...state,
        playbackRate: action.value,
      };
    }
    case 'QUALITY_UPDATED': {
      return {
        ...state,
        quality: action.value,
      };
    }
    case 'FULL_SCREEN_UPDATED': {
      return {
        ...state,
        isFullScreen: action.value,
      };
    }
    case 'EMOJI_CHAT_ENABLED_UPDATED': {
      return {
        ...state,
        scheduleDetails: action.value,
      };
    }

    case 'ASK_SAARTHI_SUBJECT_UPDATED': {
      return {
        ...state,
        askSaarthiSubject: action.value,
      };
    }
    case 'VIDEO_PLAYER_PAUSED_ON_ADD_NOTE': {
      return {
        ...state,
        videoPausedByAddNote: action.value,
      };
    }
    case 'SET_CWOC_COACH_MENTOR': {
      return {
        ...state,
        cwocCoachMentor: action.value,
      };
    }
    case 'SET_SLIDE_DATA': {
      return {
        ...state,
        slideDataContext: action.value,
      };
    }
    case 'SET_PLAYER_MODE': {
      return {
        ...state,
        playerMode: action.value,
      };
    }
    case 'SET_LOW_DATA_MODE': {
      console.log('ACTION>>>', action);
      return {
        ...state,
        isLowDataModeEnabled: action.value,
      };
    }
    case 'SETTINGS_CLICKED': {
      return {
        ...state,
        isSettingsClicked: action.value,
      };
    }
    case 'SWITCH_CLICKED': {
      return {
        ...state,
        isSettingsClicked: action.value,
      };
    }
    case 'THREE_DOTS_CLICKED': {
      return {
        ...state,
        isSettingsClicked: action.value,
      };
    }
    case 'FETCH_POLICY_DATA': {
      return {
        ...state,
        fetchPolciyData: action.value,
      };
    }
    case 'SET_DOUBT_TO_OPEN':
      return {
        ...state,
        selectedDoubtSlideId: action.payload.slideId,
        selectedDoubtId: action.payload.doubtId,
      };
  }
}

interface Props {
  scheduleId?: string;
  subjectSlug?: string;
  token?: string;
  randomId?: string;
  batchSlug?: string;
  isUnderMaintenance?: boolean;
  contentId?: string;
}

const browser = videojs.browser;

const isSafari =
  browser &&
  (browser['IS_SAFARI'] ||
    browser['IS_IOS'] ||
    browser['IS_IPAD'] ||
    browser['IS_IPHONE'] ||
    browser['IS_IPOD']);

export const PlayerProvider: FC<{ children?: ReactNode } & Props> = (props) => {
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [programId, setProgramId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [contentData, setContentData] = useState<ContentDetails>();
  const [qualityMapping, setQualityMapping] = useState(qualitiesMapping);
  const [mqttService, setMqttService] = useState<any>({
    client: MqttClient,
    subscribe: () => {},
  });
  const [isMqttConnected, setIsMqttConnected] = useState(false);
  const [isLdmEnabled, setIsLdmEnabled] = useState(true);
  const slideModeRef = useRef<string>('');
  const [state, dispatch] = React.useReducer(playerReducer, initialState);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isAskSaarthiEnabled, setIsAskSaarthiEnabled] = useState(initialState.isAskSaarthiEnabled);
  const [isPollIconActive, setIsPollIconActive] = useState<boolean>(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [switchClicked, setSwitchClicked] = useState<boolean>(false);
  const [isThreeDotsClicked, setIsThreeDotsClicked] = useState<boolean>(false);
  const {
    value: isWatchingLive,
    setValue: setIsWatchingLive,
    valueRef: isWatchingLiveRef,
  } = useStateRef<boolean>(true);

  const {
    value: activePollDetails,
    setValue: setActivePollDetails,
    valueRef: activePollDetailsRef,
  } = useStateRef<PollDetail>(new PollDetail({}));

  const {
    value: activeLastPollDetails,
    setValue: setLastActivePollDetails,
    valueRef: activeLastPollDetailsRef,
  } = useStateRef<PollDetail>(new PollDetail({}));

  // firebase config state
  const [awsHlsLiveStreamLatencyTimeInSecond, setAwsHlsLiveStreamLatencyTimeInSecond] =
    useState<number>(25);
  const [isAWSPollFeedbackEnabled, setIsAWSPollFeedbackEnabled] = useState<boolean>(false);
  const [isAWSPollToolTipEnabled, setIsAWSPollToolTipEnabled] = useState<boolean>(false);
  const stopLivePollAfterResultDeclareTime = useRef<number>(10); // SECONDS
  const stopPostPollAfterResultDeclareTime = useRef<number>(10); // SECONDS

  const [startLivePoll, setStartLivePoll] = useState(false);
  const [pollState, setPollState] = useState<string>('');
  const [showMeetIcon, setShowMeetIcon] = useState<boolean>(false);
  const [showMeetModal, setShowMeetModal] = useState<boolean>(false);
  const [joinMeetData, setJoinMeetData] = useState<any>();
  const [leaderBoardStats, setLeaderBoardStats] = useState<GeneratedLeaderBoardModel>(
    new GeneratedLeaderBoardModel({}),
  );
  const [currentUserResult, setCurrentUserResult] = useState(new LeaderBoardModel({}));
  const [activeSaarthiId, setActiveSaarthiId] = useState<string>('');
  const [userPollStats, setUserPollStats] = useState<SelfStats>(new SelfStats({}));
  const [activePollId, setActivePollId] = useState<string>('');
  const [voteModal, setVoteModal] = useState<boolean>(false);
  const [activePollResults, setActivePollResults] = useState<GeneratedResultModel>(
    new GeneratedResultModel({}),
  );
  const [timeless, setTimeless] = useState<boolean>(false);
  const [timerValue, setTimerValue] = useState<number>();
  const [saarthiActivePlan, setSaarthiActivePlan] = useState<any>();
  const [wasCancelClicked, setWasCancelClicked] = useState<any>();
  const playbackRateRef = useRef(null as any);
  const [modeUpdate, setModeUpdate] = useState('');
  const [uSocketEnabledChat, setUsocketEnabledChat] = useState<boolean | undefined>(undefined);
  const [uSocketEnabledPlayer, setUsocketEnabledPlayer] = useState<boolean | undefined>(undefined);
  const [mqttEnabled, setMqttEnabled] = useState<boolean | undefined>(undefined);
  const [mqttEmojiEnabled, setMqttEmojiEnabled] = useState<boolean | undefined>(undefined);
  const [batchBlockStateChange, setBatchBlockStateChange] = useState(false);
  const [reschedule, setReschedule] = useState(false);
  const [time, setTime] = useState<any>();
  const [chatBlockSpamPolicy, setChatBlockSpamPolicy] = useState<string>('');
  const [newPlaybackRate, setNewPlaybackRate] = useState(1);
  const [showEmojiChat, setShowEmojiChat] = useState<boolean>(false);
  const [isAwsHlsLiveStreamingEnded, setIsAwsHlsLiveStreamingEnded] = useState<boolean>(false);
  const [leaveClassFriction, setLeaveClassFriction] = useState<boolean>(false);
  const videoStartRef = useRef<number>(0);
  const manifestUrlRef = useRef<string>('');
  const isStudentAttemptingLivePollRef = useRef<boolean>(false);
  const livePollCutomStopEventTimerRef = useRef<NodeJS.Timeout>();
  const {
    clientType,
    src,
    type,
    cameFrom,
    videoId,
    vType,
    childId,
    parentId,
    secondaryParentId,
    secondaryChildId,
    entityId,
    cameFromQBG,
    isWebRTCVideoProcessing,
    metadata,
    isFromCjr,
    contentId,
  } = useSelector(selectPlayerParams);

  const [showPollFeedbackRateUsButton, setShowPollFeedbackRateUsButton] = useState<string>('');
  const [disableCMAF, setDisableCMAF] = useState<boolean>(false);
  // ALL POST POLL STATES
  // POST POLL AVAILABILITY MAP
  const postPollAvailabilityMapInSeconds = useRef(new Map());
  // POST POLL DATA MAP WITH POLL ID
  const unAttemptedPollDataMap = useRef(new Map());
  // POST POLL RESULT MAP
  const postPollResultMap = useRef(new Map());
  // POST POLL STOP/CLOSE MAP
  const postPollCloseMap = useRef(new Map());
  const postPollCloseTimerRef = useRef<any>();

  const activePostPollTimeLeftToShowResultTimerRef = useRef<string>('');
  const isPostPollFeatureEnableRef = useRef<boolean>(false);
  const qualityRef = useAsyncRefProp(state.quality);
  const isLowDataModeEnabled = useAsyncRefProp(state.isLowDataModeEnabled);

  console.log('postPollAvailabilityMapInSeconds', postPollAvailabilityMapInSeconds);
  const {
    value: activePostPollDetails,
    setValue: setActivePostPollDetails,
    valueRef: activePostPollDetailsRef,
  } = useStateRef<PollDetail>(new PollDetail({}));

  // activePostPollInterval -> [startTime,endTime]
  const activePostPollIntervalRef = useRef<[number, number]>([0, 0]);

  // Timer Left State
  const {
    value: postPollTimeLeft,
    setValue: setPostPollTimeLeft,
    valueRef: postPollTimeLeftRef,
  } = useStateRef<number>(Number.POSITIVE_INFINITY);

  // Timer Interval State Reference
  const postPollTimerRef = useRef<number>(0);

  const [activePostPollState, setActivePostPollState] = useState<string>('');
  const [activePostPollResults, setActivePostPollResults] = useState<GeneratedResultModel>(
    new GeneratedResultModel({}),
  );

  const manualExitPlayerRef = useRef<boolean>(false);

  const reduxDispatch = useDispatch();

  const {
    value: isAnotherPostPollActive,
    setValue: setIsAnotherPostPollActive,
    valueRef: isAnotherPostPollActiveRef,
  } = useStateRef<AnotherPoll>({
    pollTimeFormat: '',
    pollId: '',
  });

  const { batchDetails, scheduleDetails } = useMetaData();

  const { data: addonsData } = useAddons({
    batchSlug: batchDetails?._id as string,
    enabled:
      !!props.batchSlug &&
      batchDetails?.template?.toLowerCase() !== BATCH_TYPE.PREMIUM.toLowerCase() &&
      batchDetails?.masterBatchId === '',
  });

  const { data: activeTabData } = useActiveTab({
    batchSlug: batchDetails._id as string,
    enabled:
      !!props.batchSlug &&
      batchDetails?.template?.toLowerCase() === BATCH_TYPE.PREMIUM.toLowerCase() &&
      batchDetails?.masterBatchId !== '',
  });

  const videoContainerType =
    isSafari || isWebRTCVideoProcessing ? VideoContainerType.HLS : VideoContainerType.DASH;
  const { videoUrlDetails, videoUrlDetailsRefetching, videoUrlDetailsFetchingStatus } =
    useVideoUrlDetails(
      createVideoUrlDetailParams(
        type as UrlType,
        videoContainerType,
        src,
        videoId,
        vType,
        childId,
        parentId,
        secondaryParentId,
        secondaryChildId,
        disableCMAF,
        metadata,
        isFromCjr,
      ),
    );

  // Conditionally fetch watch stats based on video type
  // Only one hook will be enabled at a time based on vType
  const regularStats = useFetchStats({
    typeId: props.scheduleId || (contentData?._id as string) || contentId,
    userId: JSON.parse(localStorage.getItem('user') as string)?.id,
  });

  const ottStats = useOttFetchStats({
    typeId: childId as string,
    userId: JSON.parse(localStorage.getItem('user') as string)?.id,
  });

  const { data: fetchWatchStatsData, isFetched: isFetchedWatchStatsData } = isOTT()
    ? ottStats
    : regularStats;

  const { checkUnleashFeature } = useCheckUnleashFeature();

  const { handleExit, totalVideoWatchDuration, getSessionWatchSeconds, getSessionId } = usePeriodicSync({
    videoUrlDetails,
    quality: state.quality,
    qualityRef: qualityRef,
    isLowDataModeEnabled: isLowDataModeEnabled,
    isFetchedWatchStatsData,
    fetchWatchStatsData,
    batchId: batchDetails?._id,
    manualExitPlayerRef,
  });

  const updateQualityMapping = (data: any) => {
    setQualityMapping(data);
  };

  const user = getUserInfo();
  const userId = user?.id;
  const updateUnleashContext = useUnleashContext();

  const {
    data: threeDModelData,
    isLoading,
    error,
    isFetching,
    isFetched,
  } = useGet3DModels({
    ...(batchDetails && { batchId: batchDetails?._id }),
    ...(batchDetails && { subjectId: batchDetails.slug }),
    ...(scheduleDetails && { scheduleId: scheduleDetails?._id }),
  });

  const userInfo = getUserInfo();

  const handleClearAllPostPollDetails = () => {
    if (activePostPollDetailsRef.current?._id.length > 0) {
      // Clearing Previous POST POLL Timer
      clearInterval(postPollTimerRef.current);
      postPollTimerRef.current = 0;
      setPostPollTimeLeft(Number.POSITIVE_INFINITY);
    }
    handlePostPollIconBlinkStop();
    setActivePostPollDetails(new PollDetail({}));
    setActivePostPollState('');
    setActivePostPollResults(new GeneratedResultModel({}));
    removeActivePostPollSelectedAnswerFromStorage();
    // clearing poll interval [startTime,endTime]
    activePostPollIntervalRef.current = [0, 0];
    clearAnotherPostPollActive();
  };

  const clearAllPostPollAfterStreamingEnd = () => {
    handleClearAllPostPollDetails();
    isPostPollFeatureEnableRef.current = false;
    postPollAvailabilityMapInSeconds.current = new Map();
    unAttemptedPollDataMap.current = new Map();
    postPollResultMap.current = new Map();
  };

  const handleAddPostPollAvailabilityMapInSeconds = (
    startTime: number,
    endTime: number,
    pollId: string,
    operation: 'ADD' | 'REMOVE',
  ) => {
    for (let i = startTime; i < endTime; i++) {
      if (operation === 'ADD') {
        postPollAvailabilityMapInSeconds.current.set(i, pollId);
      } else if (operation === 'REMOVE') {
        postPollAvailabilityMapInSeconds.current.delete(i);
      }
    }
  };

  const handleChangeLivePollToPostPoll = (pollResult: GeneratedResultModel) => {
    if (pollResult.result.type === QUESTION_TYPE.VOTE || !isPostPollFeatureEnableRef.current)
      return;
    const isOptionSubmitted = getActivePollSelectedAnswerFromStorage()?.optionId ?? [];
    if (
      isOptionSubmitted.length === 0 &&
      (activePollDetailsRef.current._id?.length > 0 ||
        activeLastPollDetailsRef.current._id.length > 0)
    ) {
      const pollDetails = activePollDetailsRef.current._id
        ? activePollDetailsRef.current
        : activeLastPollDetailsRef.current;
      console.log('activeLastPollDetails', { pollDetails });
      const staticPollDelay = Math.floor(pollDetails.static_poll_delay / 1000); // convert MS to Second
      const pollId = pollDetails._id;
      const pollStartTimeInSecond =
        pollDetails?.poll_start_time - videoStartRef.current - staticPollDelay;
      const pollExpiryTime = pollStartTimeInSecond + pollResult?.result?.expiry_duration;
      const resultDeclareTime = Math.floor(new Date().getTime() / 1000);
      postPollResultMap.current.set(pollId, { ...pollResult, resultDeclareTime });

      handleAddPostPollAvailabilityMapInSeconds(
        pollStartTimeInSecond,
        pollExpiryTime,
        pollId,
        'ADD',
      );
      const pollData = {
        data: {
          ...pollDetails,
          expiry_duration: pollResult?.result?.expiry_duration,
          poll_start_time: pollDetails.poll_start_time - staticPollDelay,
        },
        resultDeclareTime,
      };
      unAttemptedPollDataMap.current.set(pollId, pollData);
    }
  };

  const handlePostPollIconBlinkStop = () => {
    if (isPollIconActive && !isWatchingLiveRef.current && !isStudentAttemptingLivePollRef.current) {
      setIsPollIconActive(false);
    }
  };

  useEffect(() => {
    if (
      activePostPollDetails?._id?.length > 0 &&
      activePostPollState === 'RESULT' &&
      isAnotherPostPollActive?.pollId?.length === 0
    ) {
      handlePostPollIconBlinkStop();
    }
  }, [activePostPollState]);

  const handleClearPostPollCloseTimer = () => {
    clearTimeout(postPollCloseTimerRef.current);
    postPollCloseTimerRef.current = setTimeout(() => {
      clearTimeout(postPollCloseTimerRef.current);
      handleClearAllPostPollDetails();
    }, stopPostPollAfterResultDeclareTime.current * 1000);
  };

  const clearAnotherPostPollActive = () => {
    setIsAnotherPostPollActive({ pollTimeFormat: '', pollId: '' });
  };

  const checkPostPollAvailableAtTimeStamp = (playerCurrentTime: number) => {
    const currentPollId = activePostPollDetailsRef.current._id;

    // CHECKING POST POLL AVAILABLE
    if (
      !isWatchingLiveRef.current &&
      !isStudentAttemptingLivePollRef.current &&
      isPostPollFeatureEnableRef.current &&
      currentPollId?.length === 0
    ) {
      if (postPollAvailabilityMapInSeconds.current.has(playerCurrentTime)) {
        const postPollId = postPollAvailabilityMapInSeconds.current.get(playerCurrentTime);
        if (postPollId !== isAnotherPostPollActiveRef.current.pollId) {
          setPostPollIdToLocalStorage(postPollId);
          const pollData = unAttemptedPollDataMap.current.get(postPollId)?.data ?? {};
          const scheduleStartTime: number = Math.floor(videoStartRef.current); // in second
          const postPollStartTime: number = pollData?.poll_start_time - scheduleStartTime; // in second
          setIsAnotherPostPollActive({
            pollTimeFormat: convertSecondsToHHMMSSFormat(postPollStartTime),
            pollId: postPollId,
          });
          setIsPollIconActive(true);
        }
      } else {
        handlePostPollIconBlinkStop();
        clearAnotherPostPollActive();
      }
    }

    // CHECKING POST POLL RESULT
    // if (currentPollId && !isWatchingLiveRef.current && activePostPollState === 'POLL_START') {
    //   const pollResult = postPollResultMap.current.get(activePostPollDetails?._id);
    //   const resultDeclareTime = pollResult.resultDeclareTime;
    //   if (playerCurrentTime === resultDeclareTime - videoStartRef.current) {
    //     clearTimeout(activePostPollTimeLeftToShowResultTimerRef.current);
    //     setActivePostPollState('RESULT');
    //     setActivePostPollResults(new GeneratedResultModel(pollResult));
    //   }
    // }

    // CHECKING POLL CLOSING
    if (currentPollId && !isWatchingLiveRef.current && activePostPollState === 'RESULT') {
      const pollCloseTime = postPollCloseMap.current.get(currentPollId);
      if (playerCurrentTime === pollCloseTime - videoStartRef.current) {
        clearTimeout(postPollCloseTimerRef.current);
        handleClearAllPostPollDetails();
      }
    }

    // CHECKING ANOTHER POLL ACTIVE
    if (
      !isWatchingLiveRef.current &&
      !isStudentAttemptingLivePollRef.current &&
      isPostPollFeatureEnableRef.current &&
      !!currentPollId
    ) {
      if (postPollAvailabilityMapInSeconds.current.has(playerCurrentTime)) {
        const postPollId = postPollAvailabilityMapInSeconds.current.get(playerCurrentTime);
        if (
          postPollId === currentPollId ||
          postPollId === isAnotherPostPollActiveRef.current.pollId
        ) {
          return;
        }
        const pollData = unAttemptedPollDataMap.current.get(postPollId)?.data ?? {};
        const scheduleStartTime: number = Math.floor(videoStartRef.current); // in second
        const postPollStartTime: number = pollData?.poll_start_time - scheduleStartTime; // in second
        setIsAnotherPostPollActive({
          pollTimeFormat: convertSecondsToHHMMSSFormat(postPollStartTime),
          pollId: postPollId,
        });
      } else {
        if (state.slideMode === '') {
          clearAnotherPostPollActive();
          handleClearAllPostPollDetails();
        }
      }
    }
  };

  const handleAttemptPostPoll = () => {
    const pollData = unAttemptedPollDataMap.current.get(
      isAnotherPostPollActiveRef.current.pollId,
    )?.data;
    console.log('handleAttemptPostPollData,', pollData);
    const pollStartTime: number = pollData?.poll_start_time - videoStartRef.current;
    const pollEndTime: number = pollStartTime + pollData?.expiry_duration;
    const playerCurrentTime = Math.floor(state.player.currentTime());
    handleClearAllPostPollDetails();
    if (playerCurrentTime >= pollStartTime && playerCurrentTime <= pollEndTime) {
      setShowPollFeedbackRateUsButton('');
      clearTimeout(postPollCloseTimerRef.current);
      setActivePostPollDetails({ ...new PollDetail(pollData) });
      setPostPollIdToLocalStorage(isAnotherPostPollActiveRef.current.pollId);
      setActivePostPollState('POLL_START');
      setIsPollIconActive(true);
      activePostPollIntervalRef.current = [pollStartTime, pollEndTime];
      clearAnotherPostPollActive();
    }
  };

  const parseFirebaseFlag = (value: unknown): boolean | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
    return undefined;
  };

  const updateFirebaseConfig = (data: any) => {
    if (!data) return;
    console.log('updateFirebaseConfig called', data);

    const usocketChat = parseFirebaseFlag(data.isUsocketEnabledInChat);
    if (usocketChat !== undefined) setUsocketEnabledChat(usocketChat);

    const usocketPlayer = parseFirebaseFlag(data.isUsocketEnabledInPlayer);
    if (usocketPlayer !== undefined) setUsocketEnabledPlayer(usocketPlayer);

    const mqttChat = parseFirebaseFlag(data.isMqttEnabled);
    console.log('mqttChat flag parsed:', mqttChat, '| raw:', data.isMqttEnabled);
    if (mqttChat !== undefined) setMqttEnabled(mqttChat);

    const mqttEmoji = parseFirebaseFlag(data.isMqttEmojiEnabled);
    if (mqttEmoji !== undefined) setMqttEmojiEnabled(mqttEmoji);

    // POST POLL FEATURE TOOLS CONFIG
    if (
      data?.postPollFeatureConfigData &&
      Object.keys(data?.postPollFeatureConfigData).length > 0
    ) {
      const {
        isAWSPollFeedbackEnabled,
        isAWSPollToolTipEnabled,
        livePollCloseTime,
        postPollCloseTime,
        isRecordPollFeedbackEnabled,
      } = data?.postPollFeatureConfigData ?? {};
      setIsAWSPollFeedbackEnabled(isAWSPollFeedbackEnabled);
      setIsAWSPollToolTipEnabled(isAWSPollToolTipEnabled);
      reduxDispatch(setIsPollFeedbackEnabled(isRecordPollFeedbackEnabled as boolean));
      stopLivePollAfterResultDeclareTime.current = livePollCloseTime;
      stopPostPollAfterResultDeclareTime.current = postPollCloseTime;
    }
    if (data?.awsHlsLiveStreamLatencyTimeInSecond) {
      setAwsHlsLiveStreamLatencyTimeInSecond(data?.awsHlsLiveStreamLatencyTimeInSecond);
    }
  };

  useEffect(() => {
    // Subscribe for future publishes (Firebase resolves after mount)
    pubSub.subscribe(CUSTOM_EVENT_NAMES.GOT_FIREBASE_CONFIG, updateFirebaseConfig);

    // Also await the promise in case Firebase already resolved before this
    // component mounted (common when RC is cached from a previous session)
    firebaseInitPromise.then((data) => {
      if (data) {
        updateFirebaseConfig(data);
      }
    });

    return () => {
      pubSub.unsubscribe(CUSTOM_EVENT_NAMES.GOT_FIREBASE_CONFIG, updateFirebaseConfig);
    };
  }, []);

  useEffect(() => {
    if (addonsData?.length > 0 || activeTabData?.length > 0) {
      const dataToCheck = addonsData?.length > 0 ? addonsData : activeTabData;

      if (dataToCheck?.length > 0) {
        const saarthiData = dataToCheck.filter(
          (el: any) => el?.type?.toLowerCase() === ADDONS_TYPE.SAARTHI.toLowerCase(),
        );

        if (saarthiData?.length > 0) {
          setActiveSaarthiId(saarthiData[0]?.typeId);
        }
      }
    }
  }, [addonsData, activeTabData]);

  useEffect(() => {
    if (activePostPollState === 'RESULT') {
      handleClearPostPollCloseTimer();
    }
  }, [activePostPollState]);

  useEffect(() => {
    if (!userId) return;
    const properties = {
      ...(batchDetails?._id && { batchId: batchDetails._id }),
      ...(entityId && { batchId: entityId }),
    };

    if (Object.keys(properties).length) {
      updateUnleashContext({ userId, properties });
    }
  }, [userId, batchDetails?._id, entityId]);

  const {
    data: activeSaarthiData,
    refetch: saarthiRefetch,
    isFetching: activeSaarthiFetching,
  } = useActiveSaarthi({
    saarthi_id: activeSaarthiId,
    enabled: clientType !== CLIENT_TYPE.ADMIN && activeSaarthiId.length > 0,
    ...(batchDetails?.template?.toLowerCase() === BATCH_TYPE?.PREMIUM?.toLowerCase() &&
    batchDetails?.masterBatchId !== ''
      ? { premium: true }
      : {}),
  });

  const { data: contentRelatedIds } = useIDsAssociatedWithContent({
    contentId: props.contentId as string,
    enabled: true,
  });
  const { data: contentDetails } = useContentDetails({
    contentId: props.contentId as string,
    type: 'Lecture',
    programId,
    chapterId,
    subjectId,
    topicId,
  });

  useEffect(() => {
    if (isPPAdmin() || cameFromQBG) {
      return;
    }
    checkUnleashFeature(
      { featureName: ['leave-lecture-friction'] },
      {
        onSuccess: (data: UnleashFeature[]) => {
          const isTestGroup = data?.[0]?.enabled && data?.[0]?.variantName === TESTGROUP;
          if (isTestGroup) {
            setLeaveClassFriction(isTestGroup);
          }
        },
        onError: (error: unknown) => {
          console.error('Failed to check Unleash feature:', error);
        },
      },
    );
  }, []);

  useEffect(() => {
    if (clientType !== CLIENT_TYPE.ADMIN && !!activeSaarthiId) {
      if (typeof window !== 'undefined') {
        const isPremiumCohort =
          batchDetails?.template?.toLowerCase() === BATCH_TYPE?.PREMIUM?.toLowerCase() &&
          batchDetails?.masterBatchId !== '';
        localStorage.setItem('isPremiumCohort', JSON.stringify(isPremiumCohort));
        dispatch({
          type: 'SET_CWOC_COACH_MENTOR',
          value: isPremiumCohort ? COACH_TYPE?.MENTOR : COACH_TYPE?.COACH,
        });
      }
      clientType !== 'ADMIN' && saarthiRefetch();
    }
  }, [activeSaarthiId, batchDetails]);

  useEffect(() => {
    if (contentRelatedIds?._id && contentRelatedIds?._id.length > 0) {
      if (contentRelatedIds.chapterId) {
        setChapterId(contentRelatedIds.chapterId._id);
      }
      if (contentRelatedIds.programId) {
        setProgramId(contentRelatedIds.programId._id);
      }
      if (contentRelatedIds.subjectId) {
        setSubjectId(contentRelatedIds.subjectId._id);
      }
      if (contentRelatedIds.topicId) {
        setTopicId(contentRelatedIds.topicId._id);
      }
    }
  }, [contentRelatedIds]);

  const checkForVideoStatus = (data: any) => {
    if (playbackRateRef.current > 1 && data.isLive) {
      handlePlaybackRateUpdated(1);
    }
    updateIsWatchingLive(data?.isLive);
  };

  useEffect(() => {
    playbackRateRef.current = state.playbackRate;
  }, [state.playbackRate]);

  useEffect(() => {
    pubSub.subscribe(CUSTOM_EVENT_NAMES.CHECK_FOR_IS_LIVE, checkForVideoStatus);
    return () => {
      pubSub.unsubscribe(CUSTOM_EVENT_NAMES.CHECK_FOR_IS_LIVE, checkForVideoStatus);
    };
  }, []);

  useEffect(() => {
    if (contentDetails && contentDetails.length > 0) {
      setContentData(contentDetails[0] as ContentDetails);
    }
  }, [contentDetails]);

  useEffect(() => {
    setIsAskSaarthiEnabled(
      activeSaarthiData?.[0]?._id?.length > 0 &&
        scheduleDetails?.isAskSaarthiEnabled &&
        scheduleDetails?.isBatchAskSaarthiEnabled &&
        batchDetails?.isPurchased &&
        !scheduleDetails.isLive,
    );
    if (
      activeSaarthiData?.[0]?.saarthi_plan_id &&
      Object.keys(activeSaarthiData?.[0]?.saarthi_plan_id)?.length > 0
    )
      setSaarthiActivePlan(activeSaarthiData?.[0]?.saarthi_plan_id?._id);
  }, [batchDetails, scheduleDetails, activeSaarthiData]);

  useEffect(() => {
    setShowEmojiChat(scheduleDetails?.isEmojiEnabled);
  }, [scheduleDetails]);

  useEffect(() => {
    if (uSocketEnabledChat) {
      pubSub.subscribe(EmitContext.BATCH, handleBatchBlockFromUsocket);
      return () => {
        pubSub.unsubscribe(EmitContext.BATCH, handleBatchBlockFromUsocket);
      };
    }
  }, [uSocketEnabledChat]);

  const {
    data: activePollData,
    refetch: activePollRefetch,
    isLoading: isActivePollLoading,
  } = useFetchActivePoll({
    scheduleId: scheduleDetails._id,
    isLive: scheduleDetails.isLive,
  });

  const {
    handleMQTTConnectionStatus,
    liveClassjoined,
    liveClassleft,
    pollIconClicked,
    handleUSocketConnectionState,
    scheduleUpdate,
  } = useEventTracker();

  const handleTimestampUpdated = useCallback(async () => {
    dispatch({ type: 'TIMESTAMP_UPDATED', value: 100 });
  }, [dispatch]);

  const handleAskSaarthiSubjectUpdate = useCallback(
    (value: any) => dispatch({ type: 'ASK_SAARTHI_SUBJECT_UPDATED', value }),
    [dispatch],
  );

  const handleUpdateSlideMode = useCallback(
    async (value: any) => {
      // @ts-ignore
      const player = window.player;
      if (value) {
        const event: ISessionEvent = {
          type: SessionEventTypes.SidebarOpened,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Sidebar Opened',
            videoDuration: player?.currentTime(),
            videoState: getPlayerState(),
            data: {
              newValue: value,
            },
          },
        };
        // Get the element you want to observe.
        // SessionEventrecord(event);
      } else {
        const event: ISessionEvent = {
          type: SessionEventTypes.SidebarClosed,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Sidebar Closed',
            videoDuration: player?.currentTime(),
            videoState: getPlayerState(),
            data: {
              newValue: value,
            },
          },
        };
        // Get the element you want to observe.
        // SessionEventrecord(event);
      }
      dispatch({ type: 'SLIDE_MODE_UPDATED', value });
      slideModeRef.current = value;
    },
    [dispatch],
  );

  const handleBatchBlockFromUsocket = (data: any) => {
    console.log('handleBatchBlockFromUsocket', data);
    switch (data?.operation) {
      case BatchEvents.BATCH_CHAT_BLOCK:
        if (data?.data?.userId === userInfo?.id) {
          const temp = scheduleDetails;
          temp.isBatchChatBlocked = true;
          handleBatchBlockStateChange(temp.isBatchChatBlocked);
        }
        break;
      case BatchEvents.BATCH_CHAT_UNBLOCK:
        if (data?.data?.userId === userInfo?.id) {
          const temp = scheduleDetails;
          temp.isBatchChatBlocked = false;
          handleBatchBlockStateChange(temp.isBatchChatBlocked);
        }
        break;
    }
  };

  const handleBatchBlockStateChange = useCallback(
    async (value: boolean) => {
      setBatchBlockStateChange(value);
      dispatch({ type: 'BLOCK_STATE_UPDATED', value: value });
    },
    [dispatch],
  );
  const [fetchPolicyData, setFetchPolicyData] = useState<boolean>(false);

  const handleFetchDataPolicyUpdate = useCallback(
    async (value: any) => {
      setFetchPolicyData(value);
      dispatch({ type: 'FETCH_POLICY_DATA', value: value });
    },
    [dispatch],
  );

  const handleUrlUpdated = useCallback(
    async (value: string) => {
      dispatch({ type: 'URL_UPDATED', value: value });
    },
    [dispatch],
  );

  const handleEmojiStateChange = useCallback(
    async (value: any) => {
      setShowEmojiChat(value);
      const temp = scheduleDetails;
      temp.isEmojiEnabled = value;
      dispatch({ type: 'EMOJI_CHAT_ENABLED_UPDATED', value: temp });
    },
    [dispatch],
  );
  const handleFullScreenUpdate = useCallback(
    async (value: boolean) => {
      setIsFullScreen(value);
      dispatch({ type: 'FULL_SCREEN_UPDATED', value: value });
      reduxDispatch(updateIsFullScreen(value));
    },
    [dispatch],
  );

  const handleSettingsOpen = useCallback(
    async (value: boolean) => {
      if (!value) {
        setTimeout(() => {
          // @ts-ignore
          document.activeElement.blur();
        }, 0);
      }
      setIsSettingsOpen(value);
      dispatch({ type: 'SETTINGS_CLICKED', value: value });
    },
    [dispatch],
  );

  const handleSwitch = useCallback(
    async (value: boolean) => {
      setSwitchClicked(value);
      dispatch({ type: 'SWITCH_CLICKED', value: value });
    },
    [dispatch],
  );

  const handleThreeDotsOpen = useCallback(
    async (value: boolean) => {
      setIsThreeDotsClicked(value);
      dispatch({ type: 'THREE_DOTS_CLICKED', value: value });
    },
    [dispatch],
  );

  const handleMinimalUpdated = useCallback(
    async (value: boolean) => {
      dispatch({ type: 'MINIMAL_UPDATED', value: value });
    },
    [dispatch],
  );
  const handleUpdateSlideData = useCallback(
    async (value: any) => {
      dispatch({ type: 'SET_SLIDE_DATA', value: value });
    },
    [dispatch],
  );
  useEffect(() => {
    if (mqttService && !scheduleDetails?.uWebSocketEnabled) {
      handlePollSubscribe('subscribe');
    }
    if (mqttService) {
      handle3dSlideUpload('subscribe');
      handle100MsSubscribe('subscribe');
      handleBatchBlockSubscribeFromMqtt('subscribe');
    }
  }, [mqttService]);
  const socketClient = new SocketClient();
  // socketClient.emit.bind(socketClient);
  const uSocket = useRef(socketClient);

  const [uSocketClientId, setUSocketClientId] = useState('');
  const uSocketConnected = useRef(false);

  // handling poll highlighting
  useEffect(() => {
    if (activePollDetails?._id) {
      if (!isPollIconActive && isWatchingLive) {
        setIsPollIconActive(true);
      }
      if (
        isPollIconActive &&
        !isWatchingLive &&
        !isStudentAttemptingLivePollRef?.current &&
        !isAnotherPostPollActive?.pollId &&
        !activePostPollDetails?._id
      ) {
        setIsPollIconActive(false);
      }
    } else {
      setIsPollIconActive(false);
    }
  }, [isWatchingLive, activePollDetails?._id]);

  useEffect(() => {
    if (
      uSocketEnabledPlayer &&
      scheduleDetails?._id &&
      (scheduleDetails?.urlType === 'awsVideo' || contentData?.content[0]?.videoType === 'awsVideo')
    ) {
      localStorage.setItem('schedule_room_connected', 'true');
      subscribeScheduleRoomFromUsocket();
    }
  }, [uSocketEnabledPlayer, scheduleDetails]);

  useEffect(() => {
    if (
      !uSocketEnabledPlayer &&
      scheduleDetails?._id &&
      (scheduleDetails?.urlType === 'awsVideo' || contentData?.content[0]?.videoType === 'awsVideo')
    ) {
      handleScheduleSubscribeFromMqtt();
    }
  }, [mqttService, scheduleDetails, uSocketEnabledPlayer]);

  function handleScheduleSubscribeFromMqtt() {
    const topic = getScheduleMQTTTopic(scheduleDetails?._id);
    const subscription = { topic: topic, qos: 2, raps: true };
    mqttService.subscribe(subscription, (p: any) => {
      if (p.topic === topic) {
        const operatingData = JSON.parse(p.data);
        getScheduleUpdateEvent(operatingData);
      }
    });
  }

  function subscribeScheduleRoomFromUsocket() {
    pubSub.subscribe(EmitContext.SCHEDULE, getScheduleUpdateEvent);
    return () => {
      pubSub.unsubscribe(EmitContext.SCHEDULE, getScheduleUpdateEvent);
    };
  }

  function getScheduleUpdateEvent(data: any) {
    let startTimeFromUsocket, startTimeFromMqtt;
    try {
      if (data?.operation === ScheduleEvents.RESCHEDULED) {
        startTimeFromUsocket = data?.data?.startTime;
        startTimeFromMqtt = data?.startTime;
        if (uSocketEnabledPlayer) {
          getNewTime(startTimeFromUsocket);
        } else {
          getNewTime(startTimeFromMqtt);
        }
      } else if (data?.operation === ScheduleEvents.SCHEDULE_COMPLETE) {
        console.log('SCHEDULE_COMPLETE', { data }, 'socket');
        setIsAwsHlsLiveStreamingEnded(true);
      } else if (data?.operation === ScheduleEvents.TOGGLE_LOW_DATA_MODE) {
        console.log('toggling ldm mode', data);
        const ldmEnabled = data?.data?.lowDataMode;
        if (ldmEnabled) {
          setIsLdmEnabled(true);
        } else {
          showNotification({
            title: NotificationTitles.LOW_DATA_MODE_DISABLED_TOAST,
            type: NotificationEnums.INFO,
            position: NotificationPosition.TOP_CENTER,
            duration: NotificationDuration.LONG,
          });
          setIsLdmEnabled(false);
          handlePlayerModeUpdated(false);
        }
      }
    } catch (e) {
      console.log('error in schedule update playerprovider', e);
    }
  }

  const getNewTime = (startTime: string) => {
    let hoursAndMinutes, minutes;
    const newStartTime = new Date(startTime);
    if (newStartTime.getMinutes() < 10) {
      minutes = '0' + newStartTime.getMinutes();
      hoursAndMinutes = newStartTime.getHours() + ':' + minutes;
    } else {
      hoursAndMinutes = newStartTime.getHours() + ':' + newStartTime.getMinutes();
    }
    if (newStartTime.getHours() > 12) {
      hoursAndMinutes = hoursAndMinutes + ' PM';
    } else {
      hoursAndMinutes = hoursAndMinutes + ' AM';
    }
    console.log('SCHEDULE UPDATE<>>>', startTime, newStartTime, hoursAndMinutes);
    setReschedule(true);
    setTime(hoursAndMinutes);
    scheduleUpdate(startTime);
  };

  useEffect(() => {
    const serverTime = JSON.parse(localStorage.getItem('server_time') || '{}');
    // if((serverTime && scheduleDetails?.endTime) && (new Date(serverTime).getTime() < new Date(scheduleDetails?.endTime).getTime())){
    //   window.open(window.location.host+'/study')
    //   return;
    // }
    if (
      (scheduleDetails?._id !== '' && isOTT() && scheduleDetails.isLive) ||
      (scheduleDetails?._id !== '' && batchDetails?._id !== '' && scheduleDetails.isLive)
    ) {
      connectUsocket();
      return () => {
        uSocket.current.off(U_SOCKET_CONTEXT.POLL, handleEvent);
        uSocket.current.disconnect();
        handleUSocketConnectionState('disconnect', scheduleDetails?._id);
      };
    }
  }, [scheduleDetails, batchDetails, vType]);

  // Define a custom event handler
  const handleEvent = (data: any) => {
    console.log('checking data from usocket', data);
    handlePollEvents(data, POLL_CONNECTION_TYPE.U_WEB_SOCKET);
  };

  const connectUsocket = (onConnectCB?: () => {}) => {
    const connectionOptions = {
      query: {
        userId: userInfo?.id,
        scheduleId: scheduleDetails?._id,
        batchId: batchDetails?._id,
        roomContext: U_SOCKET_CONTEXT.POLL,
      },
      token: LocalStorageInstance.getToken(),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 35,
    };

    // Connect to the WebSocket server
    uSocket.current.connect(`${process.env.REACT_APP_U_SOCKET_URL}`, connectionOptions);

    // Handle the "connect" event
    uSocket.current.on('connected', (data: any) => {
      console.log('connected new', data);
      onConnectCB?.();
      handleUSocketConnectionState('connected', scheduleDetails?._id, data?.id);
      uSocketConnected.current = true;
      setUSocketClientId(data?.id);
    });
    // Handle the "disconnect" event
    uSocket.current.on('disconnect', (data: any) => {
      handleUSocketConnectionState('disconnected', scheduleDetails?._id, uSocketClientId);
      uSocketConnected.current = false;
    });

    uSocket.current.on('error', (data: any) => {
      console.log('socket error', data);
    });

    uSocket.current.on('reconnect_failed', (data: any) => {
      console.log('reconnect_failed', data, scheduleDetails?._id);
      pubSub.publish(CUSTOM_EVENT_NAMES.USOCKET_TIME_OUT, {});
    });

    uSocket.current.on('reconnect', (data: any) => {
      activePollRefetch();
    });

    const emitContextValues: Array<EmitContext> = Object.values(EmitContext);

    emitContextValues.forEach((emitContext) => {
      const handleSocket = (event: any) => {
        console.log('usocket event received', emitContext, event);
        // TODO -- replace any with whole events type
        handleEvents(emitContext, event);
      };
      uSocket.current.on(emitContext, handleSocket);
    });
    console.log('usocket poll enabled', scheduleDetails?.uWebSocketEnabled);
    if (scheduleDetails?.uWebSocketEnabled) {
      uSocket.current.on(U_SOCKET_CONTEXT.POLL, handleEvent);
    }
  };

  const handleEvents = (emitContext: EmitContext, e: any) => {
    // TODO -- replace any with whole events type
    console.log('Socket listening:', emitContext, e?.type);
    socketListener.route(emitContext, e);
  };

  const disconnectUSocket = () => {
    uSocket.current.off(U_SOCKET_CONTEXT.POLL, handleEvent);
    uSocket.current.disconnect();
    uSocketConnected.current = false;
    handleUSocketConnectionState('disconnect', scheduleDetails?._id);
  };

  const updateLivePollData = (data: PollDetail) => {
    // clear feedback section when new poll come
    setShowPollFeedbackRateUsButton('');
    setActivePollDetails({ ...new PollDetail(data) });
    if (isWatchingLiveRef.current) {
      setIsPollIconActive(true);
      if (slideModeRef.current === 'POLL') {
        setStartLivePoll(true);
      }
    }
    setPollState('POLL_START');
    setPollIdToStorage(data?._id ?? '');
  };

  useEffect(() => {
    if (activePollData && activePollData[0]) {
      const previousPollId = getPollIdFromStorage();
      if (previousPollId !== activePollData[0]?._id) {
        removeActivePollSelectedAnswerFromStorage();
      }
      updateLivePollData(new PollDetail(activePollData[0]));
    }
  }, [activePollData]);

  useEffect(() => {
    localStorage.setItem('BATCH_DETAILS', JSON.stringify(batchDetails));
  }, [batchDetails]);

  useEffect(() => {
    localStorage.setItem('SCHEDULE_DETAILS', JSON.stringify(scheduleDetails));
    const isUnderMaintenance = props?.isUnderMaintenance;
    if (scheduleDetails.isLive && !isUnderMaintenance) {
      isPostPollFeatureEnableRef.current = scheduleDetails?.isPostPollFeatureEnable;
      if (scheduleDetails.id3Enabled) {
        const pollID3DelayTime = getValueFromFirebase(process.env.REACT_APP_POLL_DELAY_FIREBASE);
        localStorage.setItem('POLL_DELAY_TIME', pollID3DelayTime);
      }
      localStorage.setItem(
        'DATA_FOR_LIVE_CLASS_LEFT',
        JSON.stringify({
          scheduleId: scheduleDetails?._id,
          urlType: scheduleDetails?.urlType,
          startTime: scheduleDetails?.startTime,
          subjectId: props.subjectSlug,
          batchId: batchDetails?._id,
        }),
      );
      liveClassjoined({
        scheduleId: scheduleDetails?._id,
        urlType: scheduleDetails?.urlType,
        startTime: scheduleDetails?.startTime,
        batchId: batchDetails?._id,
      });
      const userInfo = getUserInfo();

      const _mqttService = createMqttService();
      setMqttService(_mqttService);
      console.log('MQTT SERIVCE>>', _mqttService);
      const MQTT_URL = process.env.REACT_APP_PW_MQTT_URL;
      _mqttService.connect(MQTT_URL, {});

      _mqttService.client.on('connect', () => {
        console.log('MQTT Connected');
        // @ts-ignore
        const player = window.player;
        const event: ISessionEvent = {
          type: SessionEventTypes.MQTTConnectionSuccess,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'MQTT Connection Success',
            videoDuration: player?.currentTime(),
            videoState: getPlayerState(),
            data: {},
          },
        };
        setIsMqttConnected(true);
        // SessionEventrecord(event);
        handleMQTTConnectionStatus('connected', scheduleDetails._id);
      });
      _mqttService.client.on('disconnect', () => {
        setIsMqttConnected(false);
        handleMQTTConnectionStatus('disconnected', scheduleDetails._id);
      });
      _mqttService.client.on('reconnect', () => {
        activePollRefetch();
        // @ts-ignore
        const player = window.player;
        const event: ISessionEvent = {
          type: SessionEventTypes.MQTTConnectionReconnectAttempt,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'MQTT Connection Reconnect',
            videoDuration: player?.currentTime(),
            videoState: getPlayerState(),
            data: {},
          },
        };
        // SessionEventrecord(event);
        handleMQTTConnectionStatus('reconnect', scheduleDetails._id);
      });
      const extraHeaders = {
        'client-type': 'WEB',
        'user-id': userInfo?.id,
        'batch-id': batchDetails?._id,
        'schedule-id': scheduleDetails?._id,
      };
    }
    return () => {
      const dataForLiveClassLeft = JSON.parse(
        localStorage.getItem('DATA_FOR_LIVE_CLASS_LEFT') as string,
      );
      if (dataForLiveClassLeft) {
        liveClassleft({
          ...dataForLiveClassLeft,
          socket_status: uSocket?.current?.isConnected ? 'connected' : 'disconnected',
        });
        localStorage.removeItem('DATA_FOR_LIVE_CLASS_LEFT');
      }
    };
  }, [scheduleDetails]);

  useEffect(() => {
    console.log('MQTT SERVICE in effect>>', mqttService.client, mqttService.client.connected);
    if (
      mqttService &&
      mqttService?.client &&
      isMqttConnected &&
      !scheduleDetails?.uWebSocketEnabled
    ) {
      handlePollSubscribe('subscribe');
    }
  }, [isMqttConnected]);
  const startBufferingCheck = () => {
    // @ts-ignore
    const player = window.player;
    if (document.getElementsByClassName('vjs-waiting')?.length) {
      // @ts-ignore
      if (!window.buffering) {
        const event: ISessionEvent = {
          type: SessionEventTypes.VideoBufferingStart,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Buffering Started',
            videoDuration: player?.currentTime(),
            videoState: 'buffering',
            data: {},
          },
        };
        // Get the element you want to observe.
        // SessionEventrecord(event);
      }
      // @ts-ignore
      window.buffering = true;
    } else {
      // @ts-ignore
      if (window.buffering) {
        const event: ISessionEvent = {
          type: SessionEventTypes.VideoBufferingEnd,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Buffering Ended',
            videoDuration: player?.currentTime(),
            videoState: 'buffering',
            data: {},
          },
        };
        // Get the element you want to observe.
        // SessionEventrecord(event);
      }
      // @ts-ignore
      window.buffering = false;
    }
    setTimeout(() => {
      startBufferingCheck();
    }, 500);
  };
  useEffect(() => {
    // @ts-ignore
    const player = window.player;
    const event: ISessionEvent = {
      type: SessionEventTypes.SessionStart,
      data: {
        timestamp: new Date().toLocaleString(),
        description: 'Session Started',
        videoDuration: player?.currentTime(),
        videoState: player?.paused() ? 'paused' : 'playing',
        data: {},
      },
    };
    // Get the element you want to observe.

    // SessionEventrecord(event);
    startBufferingCheck();
    return () => {
      // @ts-ignore
      if (window?.player?.destroy) {
        // @ts-ignore
        window.player?.destroy?.();
      }
      localStorage.removeItem('schedule_room_connected');
      LocalStorageInstance.removeItem(LocalStorageKeys.NOTES_ENABLED_BATCH_IDS);
      // @ts-ignore
      const player = window.player;
      const event: ISessionEvent = {
        type: SessionEventTypes.SessionEnd,
        data: {
          timestamp: new Date().toLocaleString(),
          description: 'Session Ended',
          videoDuration: player?.currentTime(),
          videoState: player?.paused() ? 'paused' : 'playing',
          data: {},
        },
      };
      // SessionEventrecord(event);
    };
  }, []);
  useEffect(() => {
    dispatch({
      type: 'USER_UPDATED',
      value: localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user') as string)
        : null,
    });
  }, []);

  useEffect(() => {
    const userInfo = getUserInfo();
    const ldmState = JSON.parse(localStorage.getItem('low_data_enabled') as string);
    if (modeUpdate.length > 0) {
      const data = {
        ldm: !!ldmState,
        reason: 'setting_flow',
      };
      eventTracker.ldm_toggle(data);
    }
  }, [scheduleDetails, batchDetails, modeUpdate]);

  useEffect(() => {
    // @ts-ignore
    const player = window.player;
    if (player) {
      player.on('ratechange', () => {
        console.log('Playback rate change event fired', player.playbackRate());
        setNewPlaybackRate(player.playbackRate());
        dispatch({
          type: 'PLAYBACK_RATE_UPDATED',
          value: player.playbackRate(),
        });
      });
    }
  }, [state.player]);

  useEffect(() => {
    // @ts-ignore
    const player = window.player;
    if (player) {
      player.on('play', () => {
        const event: ISessionEvent = {
          type: SessionEventTypes.VideoPlay,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Video Playing',
            videoDuration: player?.currentTime(),
            videoState: 'playing',
            data: {},
          },
        };
        // SessionEventrecord(event);
      });
      player.on('pause', () => {
        const event: ISessionEvent = {
          type: SessionEventTypes.VideoPause,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Video Paused',
            videoDuration: player?.currentTime(),
            videoState: 'paused',
            data: {},
          },
        };
        // SessionEventrecord(event);
      });

      player.on('ratechange', () => {
        console.log('Playback rate change event fired', player.playbackRate());
        dispatch({
          type: 'PLAYBACK_RATE_UPDATED',
          value: player.playbackRate(),
        });
      });
      player.on('loadstart', () => {
        const event: ISessionEvent = {
          type: SessionEventTypes.VideoLoading,
          data: {
            timestamp: new Date().toLocaleString(),
            description: 'Video Loading',
            videoDuration: player?.currentTime(),
            videoState: 'buffering',
            data: {},
          },
        };
        // SessionEventrecord(event);
      });
    }
    return () => {};
  }, [state.player]);

  const timerIdRef = useRef<any>(null);
  useEffect(() => {
    const liveToken = getLiveTokenFromStorage();
    const expireTime = getExpiryTimeFromStorage();
    const timeDifference = Math.floor((expireTime - new Date().getTime()) / 1000);
    if (liveToken != '' && timeDifference > 0) {
      setShowMeetIcon(true);
      setShowMeetModal(true);
      clearTimeout(timerIdRef.current);
      const temp = {
        token: liveToken,
        expireTime: expireTime,
      };
      setJoinMeetData(temp);
      timerIdRef.current = setTimeout(() => {
        setShowMeetModal(false);
        setShowMeetIcon(false);
        removeExpiryTimeFromStorage();
        removeLiveTokenFromStorage();
      }, timeDifference * 1000);

      return () => clearTimeout(timerIdRef.current);
    } else {
      removeLiveTokenFromStorage();
      removeExpiryTimeFromStorage();
    }
  }, []);

  const handlePlayPause = useCallback(
    async (_currentState?: boolean) => {
      handleVideoPausedByAddNoteAction(false);
      // @ts-ignore
      if (window?.player) {
        // @ts-ignore
        const currentState = !window.player?.paused();
        // @ts-ignore
        if (currentState) {
          // @ts-ignore
          window.player?.pause();
        } else {
          // @ts-ignore
          if (
            window.player.duration() === window.player.currentTime() &&
            !localStorage.getItem('session-restarted')
          ) {
            localStorage.setItem('session-restarted', 'true');
            console.log('here========');
            const myEvent = new CustomEvent(
              process.env.REACT_APP_CONVIVA_SESSION_RESTARTED_EVENT as string,
              {
                detail: {},
                bubbles: true,
                cancelable: true,
              },
            );
            document.dispatchEvent(myEvent);
          }
          // @ts-ignore
          // window.player?.play();
          requestPlayback(window.player);
        }
      }
    },
    [dispatch],
  );

  const handle100MsSubscribe = (type: string) => {
    const topic = `mqtt/liveclass/${scheduleDetails?._id}/${userInfo?.id}`;
    const subscription = { topic: topic, qos: 2, rap: 2 };
    if (type === 'subscribe') {
      mqttService?.subscribe(subscription, (p: any) => {
        if (p.topic === topic) {
          const parsedData = JSON.parse(p.data);
          hadnle100MsEvents(parsedData);
        }
      });
    } else if (type === 'unsubscribe') {
      // @ts-ignore
      mqttService.unsubscribe(subscription);
    }
  };
  const timeoutIdRef = useRef<any>(null);
  const handle100MSEventsReceived = () => {
    const data = {
      userName: userInfo?.firstName + ' ' + userInfo?.lastName,
      subjectId: scheduleDetails?.subject?._id,
      scheduleId: scheduleDetails?._id,
      batchName: batchDetails?.name,
      batchId: batchDetails?._id,
    };
    logEvent(EventType.HUNDRED_MS_INVITE_RECEIVED, data);
  };
  const hadnle100MsEvents = (data: any) => {
    setShowMeetIcon(true);
    setShowMeetModal(true);
    setJoinMeetData(data);
    const timeDifference = Math.floor((data?.expireTime - new Date().getTime()) / 1000);
    clearTimeout(timeoutIdRef.current);
    setLiveTokenToStorage(data?.token);
    setExpiryTimeToStorage(data?.expireTime);
    setWasCancelClicked(false);
    handle100MSEventsReceived();

    timeoutIdRef.current = setTimeout(() => {
      setShowMeetModal(false);
      setShowMeetIcon(false);
      removeLiveTokenFromStorage();
      removeExpiryTimeFromStorage();
    }, timeDifference * 1000);
  };

  const handlePlayVideo = () => {
    console.log('handlePlayVideo');
    // @ts-ignore
    if (window?.player) {
      // @ts-ignore
      // window.player?.play();
      requestPlayback(window.player);
    }
  };

  const handlePauseVideo = () => {
    // @ts-ignore
    if (window?.player) {
      // @ts-ignore
      window.player?.pause();
    }
  };

  const handleBackward = useCallback(async () => {
    // @ts-ignore
    if (window.player) {
      // @ts-ignore
      window?.player?.currentTime(Math.max(window?.player?.currentTime() - 10, 0));
    }
  }, [dispatch]);
  const handleForward = useCallback(async () => {
    // @ts-ignore
    if (window.player) {
      // @ts-ignore
      window?.player?.currentTime(
        // @ts-ignore
        Math.min(window?.player?.currentTime() + 10, window.player.duration()),
      );
    }
  }, [dispatch]);

  const handleVideoPausedByAddNoteAction = useCallback(
    async (value: boolean) => {
      dispatch({ type: 'VIDEO_PLAYER_PAUSED_ON_ADD_NOTE', value: value });
    },
    [dispatch],
  );

  const handleVolumeUpdated = useCallback(
    async (volume: number) => {
      // @ts-ignore
      if (window.player) {
        // @ts-ignore
        window.player.volume(volume / 100);
      }
    },
    [dispatch],
  );
  const handleNotesTimeStamp = useCallback(
    async (_currentState?: number) => {
      // @ts-ignore
      if (window.player) {
        // @ts-ignore
        window.player.currentTime(_currentState);
      }
    },
    [dispatch],
  );
  const handlePlayerModeUpdated = useCallback(
    async (value: boolean) => {
      const data = value ? 'LowData' : 'Normal';
      localStorage.setItem('low_data_enabled', JSON.stringify(value));

      // @ts-ignore
      if (window.player) {
        // @ts-ignore
        dispatch({ type: 'SET_PLAYER_MODE', data });
        dispatch({ type: 'SET_LOW_DATA_MODE', value });
        // if(!value && uSocket.current.isConnected){
        //   disconnectUSocket();
        // }
        if (value) {
          setModeUpdate('ldmOn');
        } else {
          setModeUpdate('ldmOff');
        }
      }
    },
    [dispatch],
  );

  const handlePlaybackRateUpdated = useCallback(
    async (value: number) => {
      // @ts-ignore
      if (window.player) {
        // @ts-ignore
        window.player.playbackRate(value);
        setNewPlaybackRate(value);
        dispatch({ type: 'PLAYBACK_RATE_UPDATED', value });
      }
    },
    [dispatch],
  );

  const handleBatchBlockSubscribeFromMqtt = (type: string) => {
    if (uSocketEnabledChat) {
      return;
    }
    const topic = getBatchMQTTTopic(batchDetails?._id);
    const subscription = { topic: topic, qos: 2, rap: 2 };
    if (type === 'subscribe') {
      mqttService?.subscribe(subscription, (p: any) => {
        if (p.topic === topic) {
          const parsedData = JSON.parse(p.data);
          handleBatchBlockFromUsocket(parsedData);
        }
      });
    }
  };

  const handlePollSubscribe = (type: string) => {
    const topic = `schedule/${scheduleDetails?._id}/poll`;
    console.log('MQTT SERVICE2>>', mqttService, type);
    const subscription = { topic: topic, qos: 2, rap: 2 };
    // @ts-ignore
    const player = window.player;
    if (type === 'subscribe') {
      const event: ISessionEvent = {
        type: SessionEventTypes.MQTTSubscribe,
        data: {
          timestamp: new Date().toLocaleString(),
          description: 'MQTT Subscribe for POLL',
          videoDuration: player?.currentTime(),
          videoState: getPlayerState(),
          data: {},
        },
      };
      // SessionEventrecord(event);
      // @ts-ignore
      mqttService?.subscribe(subscription, (p: any) => {
        if (p.topic === topic) {
          const event: ISessionEvent = {
            type: SessionEventTypes.POLLReceivedMQTT,
            data: {
              timestamp: new Date().toLocaleString(),
              description: 'POLL Received from MQTT',
              videoDuration: player?.currentTime(),
              videoState: getPlayerState(),
              data: JSON.parse(p.data?.toString() || JSON.stringify({})),
            },
          };
          // SessionEventrecord(event);
          handlePollEvents(p.data.toString(), POLL_CONNECTION_TYPE.MQTT);
        }
      });
    } else if (type === 'unsubscribe') {
      const event: ISessionEvent = {
        type: SessionEventTypes.MQTTUnSubscribe,
        data: {
          timestamp: new Date().toLocaleString(),
          description: 'MQTT Un Subscribe for POLL',
          videoDuration: player?.currentTime(),
          videoState: getPlayerState(),
          data: {},
        },
      };
      // @ts-ignore
      mqttService.unsubscribe(subscription);
    }
  };

  const clearPollData = () => {
    setActivePollDetails(new PollDetail({}));
    setStartLivePoll(false);
    setActivePollId('');
    setActivePollResults(new GeneratedResultModel({}));
  };

  const handleLivePollStopExripryEvent = () => {
    setPollState('STOP_EXPIRY');
    setTimeless(false);
    setTimerValue(0);
    setVoteModal(false);
  };

  const handleLivePollResultEvent = (data: any) => {
    setPollState('RESULT');
    const setUserResult = async () => {
      await showUserResult(data?.data?._id || data?.pollId);
      await showUserStats(scheduleDetails?._id);
      await showScheduledLeaderBoard(scheduleDetails?._id);
    };
    setUserResult();
    setActivePollResults(new GeneratedResultModel(data.data));
    const pollIdTemp = getPollIdFromStorage();
    if (pollIdTemp.toString() === data?.pollId) {
      setActivePollId(data?.pollId);
    }
    // Convert live poll to post poll if poll not attempted
    handleChangeLivePollToPostPoll(new GeneratedResultModel(data.data));
  };

  const handleLivePollStopEvent = (dataFromMqtt: any) => {
    if (isPostPollFeatureEnableRef.current) {
      // Checking custom stop event before webSocket Stop Event
      clearTimeout(livePollCutomStopEventTimerRef.current);
      const isOptionSubmitted = getActivePollSelectedAnswerFromStorage()?.optionId ?? [];
      if (isOptionSubmitted?.length === 0 && !dataFromMqtt?.softDelete) {
        const pollId = dataFromMqtt?.pollId ?? '';
        postPollCloseMap.current.set(pollId, Math.floor(new Date().getTime() / 1000));
      }
    }
    setIsPollIconActive(false);
    clearPollData();
    setPollState('');
    setTimeless(false);
    setTimerValue(0);
    setVoteModal(false);
    isStudentAttemptingLivePollRef.current = false;
  };

  const handlePollEvents = (data: any, cameFrom?: string) => {
    data = data || JSON.stringify({});
    const dataFromMqtt =
      scheduleDetails?.uWebSocketEnabled && cameFrom !== POLL_CONNECTION_TYPE.ID3
        ? data
        : JSON.parse(data);

    if (
      scheduleDetails.id3Enabled &&
      cameFrom !== POLL_CONNECTION_TYPE.ID3 &&
      dataFromMqtt.operation === OPERATION.START
    ) {
      return;
    }
    if (
      scheduleDetails.id3Enabled &&
      cameFrom === POLL_CONNECTION_TYPE.ID3 &&
      dataFromMqtt?.data?.expiryDuration === null
    ) {
      activePollRefetch();
      return;
    }

    const stateChangeEventData = {
      poll_id: dataFromMqtt?.data?._id || dataFromMqtt?.pollId || '',
      schedule_id: scheduleDetails._id,
      received_time: Math.round(new Date().getTime() / 1000),
      start_time: dataFromMqtt?.data?.poll_start_time || dataFromMqtt?.data?.pollStartTime || '',
      current_state: dataFromMqtt?.operation,
    };
    eventTracker.pollStatusChanged(stateChangeEventData);
    console.log('vdfkjnmkjdnvfds', dataFromMqtt);
    switch (dataFromMqtt.operation) {
      case OPERATION.START: {
        const eventData = {
          poll_state: 'start',
          poll_id: dataFromMqtt?.pollId,
          schedule_id: scheduleDetails._id,
          received_time: Math.round(new Date().getTime() / 1000),
          start_time: dataFromMqtt?.data?.poll_start_time || dataFromMqtt?.data?.pollStartTime,
          player_type: 'old',
          source: 'AUTO',
          type: 'live',
        };
        const pollIdFromStorage = getPollIdFromStorage();
        if (pollIdFromStorage === '' && !!dataFromMqtt.pollId) {
          setPollIdToStorage(dataFromMqtt?.pollId);
        } else if (pollIdFromStorage !== dataFromMqtt?.pollId) {
          clearPollData();
          setPollIdToStorage(dataFromMqtt?.pollId);
          removeActivePollSelectedAnswerFromStorage();
        }
        eventTracker.pollEventReceived(eventData);
        joinPollFromMqtt(new PollDetail(dataFromMqtt.data));
        break;
      }
      case OPERATION.RESULT:
        handleLivePollResultEvent(dataFromMqtt);
        if (isPostPollFeatureEnableRef.current) {
          // clearing live poll after result to attend post poll
          livePollCutomStopEventTimerRef.current = setTimeout(() => {
            handleLivePollStopEvent(dataFromMqtt);
            clearTimeout(livePollCutomStopEventTimerRef.current);
          }, stopLivePollAfterResultDeclareTime.current * 1000);
        }
        break;
      case OPERATION.STOP:
        handleLivePollStopEvent(dataFromMqtt);
        break;
      case OPERATION.STOP_EXPIRY: {
        const pollId = getPollIdFromStorage();
        if (pollId.toString() === dataFromMqtt?.pollId) {
          setActivePollId(dataFromMqtt?.pollId);
        }
        handleLivePollStopExripryEvent();
        break;
      }
      case OPERATION.ATTEMPT:
        setActivePollId(dataFromMqtt?.pollId);
        setPollState('POLL_START');
        break;
    }
  };

  const joinPollFromMqtt = (pollData: PollDetail) => {
    // setUserOptedAnswer(0);
    updateLivePollData(new PollDetail(pollData));
    removeShowLeaderBoardClickedFromStorage();
    if (timerValue === 0) setTimeless(true);
    // startTimer();
    if (pollData.type === 'VOTE') {
      // openVoteModal();
      const res = getPollDetailsFromStorage();
      if (res && res?._id !== pollData._id) {
        setVoteModal(true);
      }
    }
    sendPollData(pollData, 'AUTO');
  };

  function sendPollData(pollData: PollDetail, source: string) {
    // Firebase send data
    const data: any = {
      poll_id: pollData?._id,
      schedule_id: scheduleDetails?._id,
      received_time: Math.round(new Date().getTime() / 1000),
      start_time: pollData?.poll_start_time,
      source,
      type: 'Live',
    };
    eventTracker.pollReceived(data);
  }

  async function showScheduledLeaderBoard(scheduleId: any) {
    console.log(activePollData);
    if (activePollData?._id?.length > 0) {
      console.log('HERE>>>');
      return;
    }
    try {
      const res = await fetchScheduledLeaderBoardFromApi(scheduleId);
      if (res && res.data) {
        const userResult = new GeneratedLeaderBoardModel(res.data);

        setLeaderBoardStats(userResult);
      }
    } catch (e: any) {
      console.log('error in fetching leaderboard', e);
    }
  }

  const [showThreeDModel, setShowThreeDModel] = useState<Model[]>(threeDModelData?.models || []);
  const [showThreeDModelSlidemaker, setShowThreeDModelSlidemaker] = useState<Model[]>(
    threeDModelData?.models || [],
  );

  const { showNotification } = useNotify();

  useEffect(() => {
    if (threeDModelData?.models) {
      const slidemakerModels = threeDModelData.models.filter((model) => model.isSlidemakerAsset);

      const otherModels = threeDModelData.models.filter((model) => !model.isSlidemakerAsset);

      setShowThreeDModelSlidemaker(slidemakerModels);
      setShowThreeDModel(otherModels);
    }
  }, [isFetched]);

  const handle3dSlideUpload = (type: string) => {
    const topic = `schedule/${scheduleDetails?._id}/three_d_model`;
    const subscription = { topic, qos: 2, rap: true };
    if (type === 'subscribe') {
      mqttService?.subscribe(subscription, (p: any) => {
        if (p?.topic === topic) {
          handle3dSlideAction(p?.data.toString());
        }
      });
    } else if (type === 'unsubscribe') {
      mqttService?.unsubscribe(subscription);
    }
  };

  const handle3dSlideAction = (data: any) => {
    const dataFromMqtt = JSON.parse(data);
    if (dataFromMqtt) {
      switch (dataFromMqtt?.operation) {
        case 'create':
          if (dataFromMqtt?.model?.isSlidemakerAsset) {
            setShowThreeDModelSlidemaker((prev) => {
              return prev?.some((model) => model?._id === dataFromMqtt?.model?._id)
                ? prev
                : [...prev, new cModel(dataFromMqtt?.model)];
            });
          } else {
            setShowThreeDModel((prev) => {
              return prev?.some((model) => model?._id === dataFromMqtt?.model?._id)
                ? prev
                : [...prev, new cModel(dataFromMqtt?.model)];
            });
          }
          break;

        case 'delete':
          if (dataFromMqtt?.model?.isSlidemakerAsset) {
            setShowThreeDModelSlidemaker((prev) => {
              if (prev?.length > 0) {
                const updateData = prev?.filter((p) => p?._id !== dataFromMqtt?.model?._id);
                if (updateData?.length === 0) {
                  handleUpdateSlideMode('');
                }
                return updateData;
              } else return prev;
            });
          } else {
            setShowThreeDModel((prev) => {
              if (prev?.length > 0) {
                const updateData = prev?.filter((p) => p?._id !== dataFromMqtt?.model?._id);
                if (updateData?.length === 0) {
                  handleUpdateSlideMode('');
                }
                return updateData;
              } else return prev;
            });
          }
          break;
      }
    }
  };

  async function showUserStats(pollId: any) {
    console.log('HERER>>', activePollData);
    if (activePollData?._id?.length > 0) {
      return;
    }
    try {
      const res = await fetchUserPollStatsFromApi(pollId);
      if (res && res.data) {
        const userResult = new SelfStats(res.data);
        setUserPollStats(userResult);
      }
    } catch (e: any) {
      console.log('error in fetching user stats', e);
    }
  }

  async function showUserResult(pollId: any) {
    console.log('HERE<<', pollId);
    if (pollId.length > 0) {
      try {
        const res = await fetchUserPollResultFromApi(pollId);
        if (res && res.data) {
          const userResult = new LeaderBoardModel(res.data);
          console.log('USER RESULT>>', userResult);
          setCurrentUserResult(userResult);
        }
      } catch (e: any) {
        if (e?.message?.includes('User Not participated')) {
          setCurrentUserResult(new LeaderBoardModel({}));
        }
      }
    }
  }

  const updateIsWatchingLive = (value: boolean) => {
    setIsWatchingLive(value);
  };

  const handleQualityUpdated = useCallback(
    async (value: any, index?: any) => {
      setTimeout(() => {
        // @ts-ignore
        document.activeElement.blur();
      }, 0);
      // @ts-ignore
      if (window.player) {
        pubSub.publish(CUSTOM_EVENT_NAMES.VIDEO_QUALITY_CHANGE, { quality: value });
        // @ts-ignore
        if (window.videoStats && window.watchStats && window.watchStats.stats) {
          // @ts-ignore
          window.videoStats.watchSessions[window.videoStats.watchSessions.length - 1].watchStats = [
            ...window.watchStats.stats,
          ];
          // Storing previous watchStats to calculate Learn2Earn Xp points
          let oldWatchStats = JSON.parse(localStorage.getItem('oldWatchStats') || '[]');
          // @ts-ignore
          oldWatchStats = [...oldWatchStats, ...window?.watchStats?.stats];
          localStorage.setItem('oldWatchStats', JSON.stringify(oldWatchStats));
          // @ts-ignore
          window.videoStats.watchSessions.push({
            deviceType: 'Desktop',
            medium: 'Web',
            isLive: (scheduleDetails && scheduleDetails.isLive) || false,
            videoType: scheduleDetails && scheduleDetails.urlType,
            clientVersion: '0.0.1',
            videoQuality: state.isLowDataModeEnabled
              ? 'ldm'
              : `${value}`.toLowerCase() === 'auto'
                ? 'auto_old'
                : value,
            watchStats: [],
          });

          // @ts-ignore
          window.watchStats.stats = [];
          // @ts-ignore
          window.player.pause();
          localStorage.setItem('watchStats', JSON.stringify([]));
          // @ts-ignore
          // window.player.play();
          requestPlayback(window.player);
        }

        if (scheduleDetails.isAwsHlsLivePlayer) {
          dispatch({ type: 'QUALITY_UPDATED', value: value });
          // @ts-ignore
          // const index = window.qualityLevel.findIndex((quality: number) => quality === value);
          const indexOfQuality = window?.player?.hls?.levels?.findIndex(
            (quality: any) => quality.height === value,
          );
          console.log('changeQuality', value, indexOfQuality, window?.player?.hls?.levels);
          window.player.hls.levelLoad = indexOfQuality;
          // @ts-ignore
          return window.player.changeQuality?.(indexOfQuality);
        }
        // @ts-ignore
        const tech = window?.player?.tech()?.vhs || window?.player?.tech()?.hls;

        if (tech) {
          const masterDetails = tech.playlists.master;
          const representations = masterDetails?.playlists ?? [];
          const selectedRepresentation = representations.find((r: any) => {
            return r.attributes?.RESOLUTION?.height === value;
          });
          if (!selectedRepresentation) {
            value = 'Auto';
            // @ts-ignore
            window.player.qualityLevels().levels_.forEach((quality: any) => {
              quality.enabled = true;
            });
          }

          if (selectedRepresentation) {
            // @ts-ignore
            window?.player?.qualityLevels().levels_.forEach((quality: any) => {
              quality.enabled = quality.height === value;
            });
            tech.playlists.media(selectedRepresentation);
          }

          dispatch({ type: 'QUALITY_UPDATED', value: value });
        }
      }
    },
    [dispatch, scheduleDetails, state.isLowDataModeEnabled],
  );
  const triggerRefetchActivePoll = () => {
    activePollRefetch();
  };

  const handlePlayerUpdated = useCallback(
    async (value: any) => {
      // @ts-ignore
      window.player = value;
      dispatch({ type: 'PLAYER_UPDATED', value: value });
    },
    [dispatch],
  );

  useEffect(() => {
    if (state.slideMode === 'POLL') {
      if (!activePollDetails._id || activePollDetails._id.length === 0) {
        activePollRefetch();
        if (activePollData?._id?.length > 0) {
          sendPollData(activePollData, 'MANUAL');
        }
      }
      pollIconClicked({
        activePollId: activePollDetails?._id,
        poll_start_time: activePollDetails?.poll_start_time,
        socket_status: scheduleDetails?.uWebSocketEnabled
          ? uSocket.current.isConnected
            ? 'connected'
            : 'disconnected'
          : '',
        schedule_id: scheduleDetails?._id,
        connection_id: uSocketClientId || '',
      });
      showScheduledLeaderBoard(scheduleDetails?._id);
      showUserStats(scheduleDetails?._id);
      // setGetDataFromApi(false);
    }

    // handling poll feedback when slide mode change
    if (state.slideMode === '' && showPollFeedbackRateUsButton) {
      setShowPollFeedbackRateUsButton('');
    }
    // handle clear post poll when it's outside the poll duration and slidemode is close
    if (activePostPollDetails?._id?.length > 0 && isPostPollFeatureEnableRef.current) {
      const [startTime, endTime] = activePostPollIntervalRef.current || [];
      const playerCurrentTime = state.player.currentTime();
      const isOutside = playerCurrentTime < startTime || playerCurrentTime > endTime;
      console.log(
        'startTime',
        startTime,
        endTime,
        'playerCurrentTime',
        playerCurrentTime,
        isOutside,
      );
      if (isOutside) {
        handleClearAllPostPollDetails();
      }
    }
  }, [state.slideMode]);

  const getWhiteboardData = async (
    startTime: number,
    endTime: number,
    fromSlideChange: boolean = false,
    cb: any,
  ) => {
    try {
      const data = await uSocket.current?.emitAndAwait('whiteboard', {
        type: 'FE',
        timestamp: { startTime, endTime, fromSlideChange },
      });
      console.log('WHITEBOARD DATA>>', data);
      return data;
    } catch (error) {
      logger.error('Error in fetching whiteboard data:', error);
      return { data: [] };
    }
  };

  const clearNotificationDoubt = React.useCallback(() => {
    dispatch({
      type: 'SET_DOUBT_TO_OPEN',
      payload: { slideId: '', doubtId: '' },
    });
  }, [dispatch]);

  const value = useMemo(
    () => ({
      ...state,
      manualExitPlayerRef,
      fetchWatchStatsData,
      isFetchedWatchStatsData,
      handleUrlUpdated,
      setIsPollIconActive,
      isPollIconActive,
      handlePlayPause,
      handlePlayVideo,
      handlePauseVideo,
      handleBackward,
      handlePlaybackRateUpdated,
      handleMinimalUpdated,
      handleVolumeUpdated,
      handleQualityUpdated,
      handleUpdateSlideMode,
      handleNotesTimeStamp,
      isFullScreen,
      videoUrlDetails,
      videoUrlDetailsRefetching,
      handleForward,
      handlePlayerUpdated,
      handleTimestampUpdated,
      mqttService,
      handleEmojiStateChange,
      handleFetchDataPolicyUpdate,
      handleFullScreenUpdate,
      activeSaarthiData,
      isAskSaarthiEnabled,
      currentUserResult,
      activePollDetails,
      pollState,
      leaderBoardStats,
      userPollStats,
      activePollId,
      activePollResults,
      voteModal,
      timerValue,
      timeless,
      setVoteModal,
      topicId,
      chapterId,
      programId,
      subjectId,
      contentData,
      handleVideoPausedByAddNoteAction,
      handleAskSaarthiSubjectUpdate,
      handlePollEvents,
      triggerRefetchActivePoll,
      handleUpdateSlideData,
      saarthiActivePlan,
      showThreeDModel,
      threeDModelData,
      showMeetIcon,
      setShowMeetIcon,
      showMeetModal,
      setShowMeetModal,
      joinMeetData,
      wasCancelClicked,
      setWasCancelClicked,
      updateQualityMapping,
      qualityMapping,
      setUsocketEnabledChat,
      setUsocketEnabledPlayer,
      setMqttEnabled,
      mqttEnabled,
      uSocketEnabledChat,
      uSocketEnabledPlayer,
      batchBlockStateChange,
      setBatchBlockStateChange,
      handleBatchBlockStateChange,
      chatBlockSpamPolicy,
      fetchPolicyData,
      setFetchPolicyData,
      handlePlayerModeUpdated,
      getWhiteboardData,
      isSettingsOpen,
      handleSettingsOpen,
      handleSwitch,
      handleThreeDotsOpen,
      isThreeDotsClicked,
      switchClicked,
      uSocket,
      reschedule,
      setReschedule,
      time,
      setTime,
      setNewPlaybackRate,
      newPlaybackRate,
      mqttEmojiEnabled,
      showThreeDModelSlidemaker,
      showEmojiChat,
      setShowEmojiChat,
      isAWSPollFeedbackEnabled,
      isAWSPollToolTipEnabled,
      isWatchingLive,
      activePostPollDetails,
      setActivePostPollDetails,
      isAnotherPostPollActiveRef,
      isAnotherPostPollActive,
      activePostPollResults,
      setActivePostPollResults,
      checkPostPollAvailableAtTimeStamp,
      postPollAvailabilityMapInSeconds,
      unAttemptedPollDataMap,
      postPollResultMap,
      postPollCloseMap,
      activePostPollState,
      setActivePostPollState,
      isWatchingLiveRef,
      videoStartRef,
      manifestUrlRef,
      updateIsWatchingLive,
      handleClearAllPostPollDetails,
      handleAddPostPollAvailabilityMapInSeconds,
      handleAttemptPostPoll,
      activePostPollDetailsRef,
      setIsAnotherPostPollActive,
      activePostPollTimeLeftToShowResultTimerRef,
      isStudentAttemptingLivePollRef,
      isPostPollFeatureEnableRef,
      clearAllPostPollAfterStreamingEnd,
      startLivePoll,
      setStartLivePoll,
      postPollTimerRef,
      postPollTimeLeftRef,
      postPollTimeLeft,
      setPostPollTimeLeft,
      isAwsHlsLiveStreamingEnded,
      stopPostPollAfterResultDeclareTime,
      awsHlsLiveStreamLatencyTimeInSecond,
      showPollFeedbackRateUsButton,
      setShowPollFeedbackRateUsButton,
      activePostPollIntervalRef,
      clearAnotherPostPollActive,
      setLastActivePollDetails,
      uSocketConnected,
      isLdmEnabled,
      videoUrlDetailsFetchingStatus,
      setDisableCMAF,
      leaveClassFriction,
      handleExit,
      totalVideoWatchDuration,
      getSessionWatchSeconds,
      getSessionId,
      clearNotificationDoubt,
    }),
    [
      state,
      manualExitPlayerRef,
      fetchWatchStatsData,
      isFetchedWatchStatsData,
      mqttService,
      handleUrlUpdated,
      handleQualityUpdated,
      handlePlayerUpdated,
      handleBackward,
      handlePlayVideo,
      handlePauseVideo,
      setIsPollIconActive,
      isPollIconActive,
      videoUrlDetails,
      videoUrlDetailsRefetching,
      isFullScreen,
      triggerRefetchActivePoll,
      handlePlaybackRateUpdated,
      handleMinimalUpdated,
      handleUpdateSlideMode,
      handleVolumeUpdated,
      handleForward,
      handleTimestampUpdated,
      handlePlayPause,
      handleEmojiStateChange,
      handleFetchDataPolicyUpdate,
      handleFullScreenUpdate,
      activeSaarthiData,
      isAskSaarthiEnabled,
      activePollDetails,
      pollState,
      leaderBoardStats,
      userPollStats,
      activePollId,
      activePollResults,
      voteModal,
      timerValue,
      timeless,
      setVoteModal,
      handleVideoPausedByAddNoteAction,
      currentUserResult,
      topicId,
      chapterId,
      programId,
      subjectId,
      contentData,
      handleNotesTimeStamp,
      handleAskSaarthiSubjectUpdate,
      handlePollEvents,
      handleUpdateSlideData,
      saarthiActivePlan,
      showThreeDModel,
      threeDModelData,
      showMeetIcon,
      setShowMeetIcon,
      showMeetModal,
      setShowMeetModal,
      joinMeetData,
      wasCancelClicked,
      setWasCancelClicked,
      updateQualityMapping,
      qualityMapping,
      setUsocketEnabledChat,
      setUsocketEnabledPlayer,
      setMqttEnabled,
      mqttEnabled,
      uSocketEnabledChat,
      uSocketEnabledPlayer,
      batchBlockStateChange,
      setBatchBlockStateChange,
      handleBatchBlockStateChange,
      chatBlockSpamPolicy,
      fetchPolicyData,
      setFetchPolicyData,
      handlePlayerModeUpdated,
      getWhiteboardData,
      isSettingsOpen,
      handleSettingsOpen,
      handleSwitch,
      handleThreeDotsOpen,
      isThreeDotsClicked,
      switchClicked,
      uSocket,
      reschedule,
      setReschedule,
      time,
      setTime,
      setNewPlaybackRate,
      newPlaybackRate,
      mqttEmojiEnabled,
      showThreeDModelSlidemaker,
      showEmojiChat,
      setShowEmojiChat,
      isWatchingLive,
      isWatchingLiveRef,
      setIsWatchingLive,
      activePostPollDetails,
      isAnotherPostPollActive,
      activePostPollResults,
      checkPostPollAvailableAtTimeStamp,
      activePostPollState,
      setActivePostPollState,
      startLivePoll,
      postPollTimeLeft,
      isAwsHlsLiveStreamingEnded,
      awsHlsLiveStreamLatencyTimeInSecond,
      showPollFeedbackRateUsButton,
      activePostPollIntervalRef,
      setLastActivePollDetails,
      uSocketConnected,
      isLdmEnabled,
      videoUrlDetailsFetchingStatus,
      setDisableCMAF,
      leaveClassFriction,
      handleExit,
      totalVideoWatchDuration,
      getSessionWatchSeconds,
      getSessionId,
      clearNotificationDoubt,
    ],
  );

  const handleDoubtNotificationReceiveEvent = (data: any) => {
    const dataEvent = {
      subject_id: scheduleDetails?.subject?._id,
      schedule_id: scheduleDetails?._id,
      batch_id: batchDetails?._id,
      ...data,
    };
    eventTracker.doubtNotificationReceiveEvent(dataEvent);
  };

  const handleDoubtNotificationOpenEvent = (data: any) => {
    const dataEvent = {
      subject_id: scheduleDetails?.subject?._id,
      schedule_id: scheduleDetails?._id,
      batch_id: batchDetails?._id,
      ...data,
    };
    eventTracker.doubtNotificationOpenEvent(dataEvent);
  };

  useEffect(() => {
    if (scheduleDetails?._id) {
      const handleDoubtSolutionEvent = (data: DoubtSolutionEventData) => {
        const currentUser = getUserInfo();
        const eventData = {
          doubt_id: data.doubtId,
          type: 'in_app',
          solution_id: data.solutionId,
        };
        if (data.userId === currentUser?.id) {
          showNotification({
            title: 'Your doubt has been answered!',
            description: 'Check answer',
            type: NotificationEnums.SUCCESS,
            duration: NotificationDuration.EXTENDED,
            position:
              state.slideMode === 'DOUBT'
                ? NotificationPosition.TOP_RIGHT
                : NotificationPosition.BOTTOM_LEFT_ABOVE_FOOTER,
            theme: Theme.LIGHT,
            onAction: () => {
              handleUpdateSlideMode('DOUBT');
              dispatch({
                type: 'SET_DOUBT_TO_OPEN',
                payload: { slideId: data.slideId, doubtId: data.doubtId },
              });
              handleDoubtNotificationOpenEvent(eventData);
            },
          });
          handleDoubtNotificationReceiveEvent(eventData);
        }
      };
      pubSub.subscribe(EmitContext.DOUBT_SOLUTION, handleDoubtSolutionEvent);
      return () => {
        pubSub.unsubscribe(EmitContext.DOUBT_SOLUTION, handleDoubtSolutionEvent);
      };
    }
  }, [scheduleDetails?._id, dispatch, showNotification]);

  return (
    <PlayerContext.Provider value={value as UsePlayerReturn}>
      {props.children}
    </PlayerContext.Provider>
  );
};

// return type should be UsePlayerReturn
export const usePlayer = (): UsePlayerReturn => {
  // const context = useContextSelector(PlayerContext, (ctx) => ctx);
  const context = React.useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider.');
  }
  return context;
};

export const ManagedPlayerContext: FC<{ children?: ReactNode & Props }> = ({
  children,
  ...props
}) => <PlayerProvider {...props}>{children}</PlayerProvider>;
